import type { PublicEvent, PublicOffering, PublicVenue, PublicVenueCatalogState } from './domain'
import { dateInTimeZone } from '@/lib/zoned-date'
import { experienceDiscoveryTags } from '@/lib/experience-metadata'

export type DiscoveryIndex = {
  venues: PublicVenue[]
  markets: DiscoveryFacet[]
  cuisines: DiscoveryFacet[]
  neighborhoods: DiscoveryFacet[]
  experiences: Array<{ venue: PublicVenue; offering: PublicOffering }>
  events: Array<{ venue: PublicVenue; event: PublicEvent }>
  privateDiningVenues: PublicVenue[]
}

export type DiscoveryFacet = {
  label: string
  slug: string
  count: number
}

export function buildDiscoveryIndex(catalog: PublicVenueCatalogState, now = new Date()): DiscoveryIndex {
  const venues = catalog.status === 'ready' ? catalog.venues : []
  return {
    venues,
    markets: facets(venues.flatMap((venue) => venue.market ? [venue.market] : [])),
    cuisines: facets(venues.flatMap((venue) => venue.cuisine ? [venue.cuisine] : [])),
    neighborhoods: facets(venues.flatMap((venue) => venue.neighborhood ? [venue.neighborhood] : [])),
    experiences: venues.flatMap((venue) => venue.offerings.filter((offering) => isPublicAccess(offering.access) && isDiscoveryExperience(offering)).map((offering) => ({ venue, offering }))),
    events: venues.flatMap((venue) => venue.events.filter((event) => isDiscoverableEvent(event, venue.timezone, now)).map((event) => ({ venue, event }))),
    privateDiningVenues: venues.filter((venue) => venue.privateEventsEnabled),
  }
}

export function filterVenuesByMarket(venues: PublicVenue[], marketSlug: string) {
  return venues.filter((venue) => slugifyDiscoveryValue(venue.market ?? '') === marketSlug)
}

export function filterVenuesByCuisine(venues: PublicVenue[], cuisineSlug: string) {
  return venues.filter((venue) => slugifyDiscoveryValue(venue.cuisine ?? '') === cuisineSlug)
}

export function filterVenuesByNeighborhood(venues: PublicVenue[], neighborhoodSlug: string) {
  return venues.filter((venue) => slugifyDiscoveryValue(venue.neighborhood ?? '') === neighborhoodSlug)
}

export function findFacetLabel(facets: DiscoveryFacet[], slug: string) {
  return facets.find((facet) => facet.slug === slug)?.label
}

export function slugifyDiscoveryValue(value: string) {
  return value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80)
}

export function isDiscoveryExperience(offering: PublicOffering) {
  if (offering.discoveryType) return offering.discoveryType === 'experience'
  const tag = offering.discoveryTag?.trim().toLowerCase()
  if (tag && experienceDiscoveryTags.some((candidate) => candidate.toLowerCase() === tag)) return tag !== 'reservation'
  return Boolean(
    (offering.bookingMethod && offering.bookingMethod !== 'free')
      || offering.menu
      || offering.fees?.length,
  )
}

export function restaurantHref(venue: PublicVenue) {
  return `/restaurants/${encodeURIComponent(venue.tenantSlug)}/${encodeURIComponent(venue.locationSlug)}`
}

export function experienceHref(venue: PublicVenue, offering: PublicOffering) {
  return `/experiences/${encodeURIComponent(venue.tenantSlug)}/${encodeURIComponent(venue.locationSlug)}/${encodeURIComponent(offering.id)}`
}

export function eventHref(venue: PublicVenue, event: PublicEvent) {
  return `/events/${encodeURIComponent(venue.tenantSlug)}/${encodeURIComponent(venue.locationSlug)}/${encodeURIComponent(event.id)}`
}

export function isDiscoverableEvent(event: PublicEvent, timeZone?: string, now = new Date()) {
  const today = dateInTimeZone(now, timeZone ?? 'UTC')
  return event.access === 'public' && event.occurrences.some((occurrence) => (
    occurrence.date >= today && (occurrence.salesStatus ?? 'open') !== 'closed'
  ))
}

export type DiscoveryCollectionSlug = 'available-tonight'

export function isDiscoveryCollectionSlug(value: string): value is DiscoveryCollectionSlug {
  return value === 'available-tonight'
}

function facets(values: string[]) {
  const counted = new Map<string, DiscoveryFacet>()
  values.forEach((label) => {
    const slug = slugifyDiscoveryValue(label)
    if (!slug) return
    const current = counted.get(slug)
    counted.set(slug, { label: current?.label ?? label.trim(), slug, count: (current?.count ?? 0) + 1 })
  })
  return [...counted.values()].sort((left, right) => right.count - left.count || left.label.localeCompare(right.label))
}

const isPublicAccess = (access: PublicOffering['access']) => access === undefined || access === 'public'
